import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { UsuarioService } from 'app/shared/services/edduca-imob/usuario/usuario.service';
import { FeedbackService } from 'app/shared/services/edduca-imob/feedback/feedback.service';
import { ImobiliariaService } from 'app/shared/services/edduca-imob/imobiliaria/imobiliaria.service';
import { TypeFeedbackEnum } from 'app/shared/components/enums/feedback-type.enum';
import { AuthService, GlobalService } from 'app/shared/services/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FeedbackResponse } from 'app/shared/services/edduca-imob/feedback/response/feedback-register.response';
import { FeedbackTemplateResponse } from 'app/shared/services/edduca-imob/feedback/response/feedback-template.response';
import { FeedbackRegisterRequest } from 'app/shared/services/edduca-imob/feedback/request/feedback-register.request';
import { NotificationService } from 'app/shared/components/notification';
import { UsuarioListResponse } from 'app/shared/services/edduca-imob/usuario/response/usuario-list.response';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { ImobiliariaForAutocompleteResponse } from 'app/shared/models/ei-models';
import { GamificacaoHandler, useMenuTracking } from 'app/shared/components/gamificacao';

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule
  ],
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss']
})
export class FeedbackComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private _authService = inject(AuthService);
  private _globalService = inject(GlobalService);
  private _usuarioService = inject(UsuarioService);
  private _feedbackService = inject(FeedbackService);
  private _imobiliariaService = inject(ImobiliariaService);
  private _notification = inject(NotificationService);
  private _gamificacaoHandler = inject(GamificacaoHandler);
  private menuTracking = useMenuTracking();

  // Tab ativa
  activeTab: 'recebidos' | 'enviados' | 'todos' = 'recebidos';

  // Dados
  feedbacksRecebidos: FeedbackResponse[] = [];
  feedbacksEnviados: FeedbackResponse[] = [];
  feedbacksImobiliaria: FeedbackResponse[] = []; // Para ADM_SISTEMA e PARCEIRO
  usuarios: UsuarioListResponse[] = [];

  // Estados
  loadingRecebidos = false;
  loadingEnviados = false;
  loadingImobiliaria = false;

  // Info do usuário
  usuarioLogadoId: number = 0;
  imobiliariaId: number = 0;
  perfilId: number = 0;

  // Modal de detalhes
  feedbackSelecionado: FeedbackResponse | null = null;
  showDetalheModal = false;

  // Modal de novo feedback
  showNovoModal = false;
  novoFeedbackForm: FormGroup;
  enviandoFeedback = false;

  // Templates de feedback (modelos pré-definidos)
  templates: FeedbackTemplateResponse[] = [];
  templateSelecionadoId: number | null = null;

  // Autocomplete de imobiliárias (para ADM_SISTEMA e PARCEIRO)
  imobiliariaCtrl = new FormControl<ImobiliariaForAutocompleteResponse | null>(null);
  imobiliariaOptions: ImobiliariaForAutocompleteResponse[] = [];
  filteredImobiliarias: ImobiliariaForAutocompleteResponse[] = [];

  // Tipos de feedback
  tiposFeedback = [
    { id: TypeFeedbackEnum.Sugestao, nome: 'Sugestão', icone: 'heroicons_outline:light-bulb', cor: 'text-blue-400' },
    { id: TypeFeedbackEnum.Critica, nome: 'Crítica', icone: 'heroicons_outline:exclamation-triangle', cor: 'text-amber-400' },
    { id: TypeFeedbackEnum.Elogio, nome: 'Elogio', icone: 'heroicons_outline:star', cor: 'text-green-400' },
  ];

  /**
   * Verifica se o usuário é ADM_SISTEMA ou PARCEIRO (modo visualização)
   */
  get isVisualizacaoMode(): boolean {
    return this.perfilId === PerfilEnum.ADM_SISTEMA || this.perfilId === PerfilEnum.PARCEIRO;
  }

  /**
   * Verifica se o usuário pode enviar feedbacks
   */
  get podeEnviarFeedback(): boolean {
    return this.perfilId === PerfilEnum.ADM_IMOBILIARIA || this.perfilId === PerfilEnum.USUARIO_IMOBILIARIA;
  }

  /**
   * Nome da imobiliária selecionada (para exibição no header)
   */
  get selectedImobiliariaName(): string | null {
    return this.imobiliariaCtrl.value?.nome || null;
  }

  constructor() {
    this.novoFeedbackForm = this._fb.group({
      usuarioRecebidoId: [null, Validators.required],
      tipoFeedbackId: [null, Validators.required],
      texto: ['', [Validators.required, Validators.minLength(10)]],
      anonimoFlag: [false],
    });
  }

  ngOnInit(): void {
    // Registrar acesso ao menu para gamificação
    this.menuTracking.registrarAcessoMenu('feedbacks');

    const user = this._authService.getUserInToken();
    if (!user) {
      this._notification.warn('Sessão não encontrada. Faça login novamente.');
      return;
    }

    // Converte para número pois JWT retorna strings
    this.usuarioLogadoId = Number(this._globalService.getIdUserLogged());
    this.imobiliariaId = Number(user.imobiliariaId);
    this.perfilId = Number(user.perfilId);

    if (this.isVisualizacaoMode) {
      // ADM_SISTEMA ou PARCEIRO: carrega lista de imobiliárias para o autocomplete
      this.activeTab = 'todos';
      this.loadImobiliarias();
    } else {
      // ADM_IMOBILIARIA ou USUARIO_IMOBILIARIA: carrega feedbacks do usuário
      this.carregarUsuarios();
      this.carregarFeedbacksRecebidos();
      this.carregarFeedbacksEnviados();
      this.carregarTemplates();
    }
  }

  private carregarTemplates(): void {
    this._feedbackService.listarTemplates().subscribe({
      next: (res) => this.templates = res ?? [],
      error: () => this.templates = []
    });
  }

  /**
   * Aplica um template ao formulário, preenchendo o texto e
   * pré-selecionando o tipo padrão (se houver). Usuário pode editar depois.
   * Quando templateId é null ("Em branco"), limpa a textarea.
   */
  aplicarTemplate(templateId: number | null): void {
    this.templateSelecionadoId = templateId;

    if (!templateId) {
      this.novoFeedbackForm.patchValue({ texto: '' });
      return;
    }

    const template = this.templates.find(t => t.id === templateId);
    if (!template) return;

    const patch: { texto: string; tipoFeedbackId?: number } = { texto: template.texto };
    if (template.tipoFeedbackPadraoId && !this.novoFeedbackForm.get('tipoFeedbackId')?.value) {
      patch.tipoFeedbackId = template.tipoFeedbackPadraoId;
    }
    this.novoFeedbackForm.patchValue(patch);
  }

  /**
   * Carrega lista de imobiliárias para o autocomplete
   */
  loadImobiliarias(): void {
    this._imobiliariaService.getAllForAutocomplete().subscribe({
      next: (imobiliarias) => {
        this.imobiliariaOptions = imobiliarias;
        this.filteredImobiliarias = imobiliarias;
      },
      error: () => {
        this._notification.error('Falha ao carregar lista de imobiliárias');
      }
    });
  }

  /**
   * Filtra imobiliárias no autocomplete
   */
  filterImobiliarias(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredImobiliarias = this.imobiliariaOptions.filter(imob =>
      imob.nome.toLowerCase().includes(value)
    );
  }

  /**
   * Exibe nome da imobiliária no input
   */
  displayImobiliariaFn(imobiliaria: ImobiliariaForAutocompleteResponse): string {
    return imobiliaria?.nome || '';
  }

  /**
   * Callback quando uma imobiliária é selecionada
   */
  onImobiliariaSelected(imobiliaria: ImobiliariaForAutocompleteResponse): void {
    this.imobiliariaId = imobiliaria.id;
    this.carregarFeedbacksImobiliaria();
  }

  /**
   * Carrega todos os feedbacks da imobiliária selecionada (para ADM_SISTEMA e PARCEIRO)
   */
  carregarFeedbacksImobiliaria(): void {
    if (!this.imobiliariaId) return;

    this.loadingImobiliaria = true;
    this._feedbackService.listarFeedbacksImobiliaria(this.imobiliariaId).subscribe({
      next: (feedbacks) => {
        this.feedbacksImobiliaria = feedbacks || [];
        this.loadingImobiliaria = false;
      },
      error: () => {
        this.feedbacksImobiliaria = [];
        this.loadingImobiliaria = false;
        this._notification.error('Erro ao carregar feedbacks da imobiliária.');
      }
    });
  }

  carregarUsuarios(): void {
    this._usuarioService.getUsuariosImobiliaria(this.imobiliariaId).subscribe({
      next: (usuarios) => {
        // Filtra o usuário logado da lista (converte para número para garantir comparação correta)
        this.usuarios = (usuarios || []).filter(u => Number(u.id) !== this.usuarioLogadoId);
      },
      error: () => this._notification.error('Erro ao carregar colaboradores.')
    });
  }

  carregarFeedbacksRecebidos(): void {
    this.loadingRecebidos = true;
    this._feedbackService.listarFeedbacksRecebidos(this.usuarioLogadoId).subscribe({
      next: (feedbacks) => {
        this.feedbacksRecebidos = feedbacks || [];
        this.loadingRecebidos = false;
      },
      error: () => {
        this.feedbacksRecebidos = [];
        this.loadingRecebidos = false;
      }
    });
  }

  carregarFeedbacksEnviados(): void {
    this.loadingEnviados = true;
    this._feedbackService.listarFeedbacksEnviados(this.usuarioLogadoId).subscribe({
      next: (feedbacks) => {
        this.feedbacksEnviados = feedbacks || [];
        this.loadingEnviados = false;
      },
      error: () => {
        this.feedbacksEnviados = [];
        this.loadingEnviados = false;
      }
    });
  }

  getTipoFeedback(tipoId: number) {
    return this.tiposFeedback.find(t => t.id === tipoId) || { id: 0, nome: 'Desconhecido', icone: 'heroicons_outline:question-mark-circle', cor: 'text-white/50' };
  }

  // Modal de Detalhes
  abrirDetalhe(feedback: FeedbackResponse): void {
    this.feedbackSelecionado = feedback;
    this.showDetalheModal = true;
  }

  fecharDetalhe(): void {
    this.showDetalheModal = false;
    this.feedbackSelecionado = null;
  }

  // Modal de Novo Feedback
  abrirNovoFeedback(): void {
    this.novoFeedbackForm.reset({ anonimoFlag: false });
    this.templateSelecionadoId = null;
    this.showNovoModal = true;
  }

  fecharNovoFeedback(): void {
    this.showNovoModal = false;
    this.templateSelecionadoId = null;
    this.novoFeedbackForm.reset({ anonimoFlag: false });
  }

  enviarFeedback(): void {
    if (this.novoFeedbackForm.invalid) {
      this.novoFeedbackForm.markAllAsTouched();
      this._notification.warn('Preencha todos os campos obrigatórios.');
      return;
    }

    this.enviandoFeedback = true;

    const payload: FeedbackRegisterRequest = {
      ...this.novoFeedbackForm.value,
      usuarioEnviadoId: this.novoFeedbackForm.value.anonimoFlag ? null : this.usuarioLogadoId
    };

    this._feedbackService.cadastrar(payload).subscribe({
      next: (response) => {
        this._notification.success('Feedback enviado com sucesso!');
        // Processar gamificação do remetente (quem enviou)
        if (response?.gamificacaoRemetente) {
          this._gamificacaoHandler.processarGamificacao(response.gamificacaoRemetente);
        }
        this.fecharNovoFeedback();
        this.carregarFeedbacksEnviados();
        this.enviandoFeedback = false;
      },
      error: () => {
        this._notification.error('Erro ao enviar feedback.');
        this.enviandoFeedback = false;
      }
    });
  }
}
